import * as Sentry from '@sentry/react'
import React from 'react'
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom'

import { CURRENT_BUILD_ID } from '@/utils/build-version/current-build-id'
import {
  applyCustomFingerprint,
  isKnownExtensionNoise,
  scrubEventProperties,
} from '@/utils/analytics'

const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const isDev = import.meta.env.MODE === 'development'

  if (!dsn) {
    if (!isDev) {
      console.warn('Sentry DSN is missing. Error tracking is disabled.')
    }
    return
  }

  Sentry.init({
    dsn,
    enabled: !isDev,
    // Previously unset — every event landed in Sentry untagged by env/version, so env separation
    // relied entirely on each environment having its own DSN/project, and release-based regression
    // detection wasn't possible at all. `MODE` here is Vite's `--mode` value, not the generic
    // dev/prod build flag - the `build-staging`/`build-preproduction`/`build-production` scripts
    // pass `--mode staging`/`--mode pre-prod`/`--mode production` respectively (see package.json),
    // so this already resolves to the granular staging/pre-prod/production/development split.
    environment: import.meta.env.MODE,
    release: CURRENT_BUILD_ID,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 0.02,
    normalizeDepth: 5,
    // Market Shop / Visitor Parking / Package Room pass an auth token via `?token=` in the URL
    // (see getTokenFromURL in @/utils) — scrub it from every error/transaction event and
    // breadcrumb so it never lands in Sentry, which captures request.url verbatim otherwise.
    beforeBreadcrumb: (breadcrumb) => scrubEventProperties(breadcrumb),
    beforeSend: (event) => {
      const scrubbed = scrubEventProperties(event)

      // Drops errors thrown by scripts that iOS browsers/extensions inject into the page (not ours)
      // — see filter-extension-noise.ts.
      if (isKnownExtensionNoise(scrubbed)) return null

      // Re-groups AxiosError issues by status + route pattern instead of Sentry's default
      // stack-derived fingerprint — see sentry-fingerprint.ts for why the default grouping was
      // splitting one recurring issue across multiple releases.
      return applyCustomFingerprint(scrubbed)
    },
  })
}

export default initSentry
